import React, { useState, useEffect } from 'react';
import { useTranslation } from '../i18n/LanguageContext';

function fmt(n, decimals = 4) {
  if (n == null) return '—';
  return parseFloat(n).toFixed(decimals);
}

function timeStr(isoStr) {
  if (!isoStr) return '';
  return new Date(isoStr).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function useClock() {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return now.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export default function LiveStatus({ analysis, openTrades, events, rsiDeadzone, market = 'futures' }) {
  const { t } = useTranslation();
  const isSpot = market === 'spot';
  const clock    = useClock();
  // Fallback YOK — analysis.strategy boş/geçersizse analysis.skipReason zaten (aşağıda) görünür
  // bir uyarı olarak gösterilir, göstergeler basitçe hiç render edilmez.
  const strategy = analysis?.strategy;

  // ── Strateji bazlı indikatör kartı ──────────────────────────
  let indCard = null;

  if (strategy === 'range_hma_adx') {
    const hmaDir      = analysis?.hmaDirection;
    const adxVal      = analysis?.adx;
    const adxThr      = analysis?.adxThreshold ?? 0;
    const adxFiltered = adxThr > 0;
    const adxOk       = !adxFiltered || (adxVal != null && adxVal >= adxThr);
    const dirLabel    = hmaDir === 1 ? t('liveStatus.up') : (hmaDir === -1 ? t('liveStatus.down') : '—');
    const dirColor    = hmaDir === 1 ? 'var(--accent)' : (hmaDir === -1 ? 'var(--red)' : 'var(--muted)');

    indCard = (
      <>
        <div className="stat-card">
          <div className="stat-label">{t('liveStatus.hmaDirection')}{strategy === 'range_hma_adx' ? t('liveStatus.rangeBarSuffix') : ''}</div>
          <div className="stat-value" style={{ color: dirColor, fontSize: 18 }}>{dirLabel}</div>
          <div className="stat-sub" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>HMA: {fmt(analysis?.hma, 4)}</span>
            {strategy === 'range_hma_adx' && analysis?.rangeBarCount != null && (
              <span>{t('liveStatus.box')} {fmt(analysis?.rangeBoxSize, 6)} · {analysis.rangeBarCount} {t('liveStatus.barSuffix')}</span>
            )}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">ADX{adxFiltered ? t('liveStatus.adxThresholdSuffix', { v: adxThr }) : t('liveStatus.adxInfoSuffix')}</div>
          <div className={`stat-value ${adxFiltered ? (adxOk ? 'text-accent' : 'text-yellow') : 'text-yellow'}`}>
            {fmt(adxVal, 1)}
          </div>
          <div className="stat-sub">
            {adxVal == null ? '—' : adxFiltered ? (adxOk ? t('liveStatus.filterPassed') : t('liveStatus.filterBlocking', { v: adxThr })) : t('liveStatus.filterOff')}
          </div>
        </div>
      </>
    );
  }

  else if (strategy === 'rsi_rsima') {
    const rsiVal    = analysis?.rsi;
    const rsiMaVal  = analysis?.rsiMa;
    const dz        = rsiDeadzone > 0 ? rsiDeadzone : 0;
    const hasOpen   = (openTrades?.length || 0) > 0;
    const openSide  = hasOpen ? openTrades[0]?.side : null;
    const buffer    = rsiMaVal != null ? (openSide === 'long' ? rsiMaVal - dz : openSide === 'short' ? rsiMaVal + dz : null) : null;
    const sideLong  = rsiVal != null && rsiMaVal != null && rsiVal >= rsiMaVal;
    const blockedShort = isSpot && !hasOpen && !sideLong && rsiVal != null && rsiMaVal != null;
    const dirLabel  = (rsiVal == null || rsiMaVal == null)
      ? '—'
      : sideLong
        ? t('liveStatus.longRsiRsima')
        : (isSpot ? t('liveStatus.shortRsiRsimaSpot') : t('liveStatus.shortRsiRsima'));
    const dirColor  = (rsiVal == null || rsiMaVal == null) ? 'var(--muted)' : (sideLong ? 'var(--accent)' : (isSpot ? 'var(--muted)' : 'var(--red)'));

    indCard = (
      <>
        <div className="stat-card">
          <div className="stat-label">{t('liveStatus.rsimaLabel')}</div>
          <div className="stat-value" style={{ color: dirColor, fontSize: 18 }}>{fmt(rsiVal, 2)}</div>
          <div className="stat-sub" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>{dirLabel}</span>
            {!hasOpen && blockedShort && (
              <span>{t('liveStatus.blockedShortSpotRsima')}</span>
            )}
            {!hasOpen && !blockedShort && (
              <span>{t('liveStatus.instantOpenRsima')}</span>
            )}
            {hasOpen && <span>{t('liveStatus.buffer', { v: buffer != null ? fmt(buffer, 2) : '—', dz })}</span>}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">{t('liveStatus.rsiMaRef')}</div>
          <div className="stat-value text-yellow">{fmt(rsiMaVal, 2)}</div>
        </div>
      </>
    );
  }

  else if (strategy === 'rsima50') {
    const rsiVal   = analysis?.rsi;
    const rsiMaVal = analysis?.rsiMa;
    const hasOpen  = (openTrades?.length || 0) > 0;
    const sideLong = rsiMaVal != null && rsiMaVal >= 50;
    const blockedShort = isSpot && !hasOpen && rsiMaVal != null && !sideLong;
    const dirLabel = rsiMaVal == null
      ? '—'
      : sideLong
        ? t('liveStatus.longRsima50')
        : (isSpot ? t('liveStatus.shortRsima50Spot') : t('liveStatus.shortRsima50'));
    const dirColor = rsiMaVal == null ? 'var(--muted)' : (sideLong ? 'var(--accent)' : (isSpot ? 'var(--muted)' : 'var(--red)'));

    indCard = (
      <>
        <div className="stat-card">
          <div className="stat-label">{t('liveStatus.rsima50Label')}</div>
          <div className="stat-value" style={{ color: dirColor, fontSize: 18 }}>{fmt(rsiMaVal, 2)}</div>
          <div className="stat-sub" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>{dirLabel}</span>
            {!hasOpen && blockedShort && <span>{t('liveStatus.blockedShortSpotRsima50')}</span>}
            {!hasOpen && !blockedShort && <span>{t('liveStatus.instantOpenRsima50')}</span>}
            {hasOpen && <span>{t('liveStatus.rsima50ExitNote')}</span>}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">RSI</div>
          <div className="stat-value text-yellow">{fmt(rsiVal, 2)}</div>
        </div>
      </>
    );
  }

  else if (strategy === 'trend_pullback_r') {
    const ma50   = analysis?.ma50;
    const ma200  = analysis?.ma200;
    const adxVal = analysis?.adx;
    const rsiVal = analysis?.rsi;
    const hasOpen = (openTrades?.length || 0) > 0;
    const openTrade = hasOpen ? openTrades[0] : null;

    const uptrend   = ma50 != null && ma200 != null && ma50 > ma200;
    const downtrend = ma50 != null && ma200 != null && ma50 < ma200;
    const trendLabel = uptrend ? t('liveStatus.uptrend') : (downtrend ? t('liveStatus.downtrend') : '—');
    const trendColor = uptrend ? 'var(--accent)' : (downtrend ? 'var(--red)' : 'var(--muted)');
    const blockedShort = isSpot && downtrend;

    indCard = (
      <>
        <div className="stat-card">
          <div className="stat-label">{t('liveStatus.trendLabel')}</div>
          <div className="stat-value" style={{ color: trendColor, fontSize: 18 }}>{trendLabel}</div>
          <div className="stat-sub" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span>MA50: {fmt(ma50, 4)} · MA200: {fmt(ma200, 4)}</span>
            {!hasOpen && analysis?.trendPullbackWaitingAway && (
              <span style={{ color: 'var(--yellow, #eab308)' }}>
                {t('liveStatus.waitingNewPullback')}
              </span>
            )}
            {!hasOpen && !analysis?.trendPullbackWaitingAway && blockedShort && <span>{t('liveStatus.blockedShortSpotTrend')}</span>}
            {!hasOpen && !analysis?.trendPullbackWaitingAway && !blockedShort && (
              <span>{analysis?.signal ? t('liveStatus.signalLabel', { v: analysis.signal.toUpperCase() }) : (analysis?.skipReason || t('liveStatus.waiting'))}</span>
            )}
            {hasOpen && (
              <span>{t('liveStatus.trendPullbackExitNote', { v: fmt(openTrade?.sl_price, 4) })}</span>
            )}
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">ADX{analysis?.adxThreshold > 0 ? t('liveStatus.adxThresholdSuffix', { v: analysis.adxThreshold }) : t('liveStatus.adxInfoSuffix')} / RSI</div>
          <div className="stat-value text-yellow" style={{ fontSize: 18 }}>{fmt(adxVal, 1)} / {fmt(rsiVal, 2)}</div>
          <div className="stat-sub">{t('liveStatus.adxRsiMomentum', { v: analysis?.rsiTrigger ?? '—' })}</div>
        </div>
      </>
    );
  }

  // ── Açık pozisyonun floating PnL'i ──────
  // Vadeli: marjin bazlı $ ve % (kaldıraç çarpanlı). Spot: kaldıraç/marjin YOK — her zaman long,
  // PnL doğrudan (anlık fiyat - giriş) × miktar; % ise basitçe fiyat değişimidir.
  const openTrade = openTrades && openTrades[0];
  let floatingPnl = null;
  if (openTrade && analysis?.currentPrice != null) {
    const entry = parseFloat(openTrade.entry_price);
    const cur   = parseFloat(analysis.currentPrice);
    if (openTrade.market === 'spot') {
      const size = parseFloat(openTrade.size);
      if (entry > 0 && size > 0) {
        const pnlPct = ((cur - entry) / entry) * 100;
        const pnlUsd = (cur - entry) * size;
        floatingPnl  = { pnlUsd, pnlPct };
      }
    } else {
      const margin = parseFloat(openTrade.margin);
      const lev    = parseFloat(openTrade.leverage);
      if (entry > 0 && margin > 0 && lev > 0) {
        const dir    = openTrade.side === 'long' ? 1 : -1;
        const pnlPct = dir * ((cur - entry) / entry) * lev * 100;
        const pnlUsd = margin * pnlPct / 100;
        floatingPnl  = { pnlUsd, pnlPct };
      }
    }
  }

  return (
    <div>
      <p className="card-title">{t('liveStatus.title')}</p>

      <div className="live-status-grid">
        {/* Anlık Fiyat */}
        <div className="stat-card">
          <div className="stat-label">{t('liveStatus.currentPrice')}</div>
          <div className="stat-value">{fmt(analysis?.currentPrice, 4)}</div>
          <div className="stat-sub">
            {analysis?.symbol || '—'} · {t('liveStatus.candleClose')}: {fmt(analysis?.candleClose, 4)}
          </div>
          {floatingPnl && (
            <div className="stat-sub" style={{ marginTop: 4 }}>
              {t('liveStatus.floatingPnl')}{' '}
              <span style={{ color: floatingPnl.pnlUsd >= 0 ? 'var(--accent)' : 'var(--red)', fontWeight: 600 }}>
                {floatingPnl.pnlUsd >= 0 ? '+' : ''}{floatingPnl.pnlUsd.toFixed(2)} USDT
                {' '}({floatingPnl.pnlPct >= 0 ? '+' : ''}{floatingPnl.pnlPct.toFixed(2)}%)
              </span>
            </div>
          )}
        </div>

        {/* Piyasa Rejimi (CHOP) — tüm stratejilerde ortak, threshold=0 ise devre dışı */}
        {analysis?.chopThreshold > 0 && (
          <div className="stat-card">
            <div className="stat-label">{t('liveStatus.marketRegime')}</div>
            <div
              className="stat-value"
              style={{ fontSize: 18, color: analysis?.chop != null && analysis.chop >= analysis.chopThreshold ? 'var(--red)' : 'var(--accent)' }}
            >
              {fmt(analysis?.chop, 2)}
            </div>
            <div className="stat-sub">
              {t('liveStatus.threshold')} {fmt(analysis.chopThreshold, 1)}
              {analysis?.chop != null && (
                analysis.chop >= analysis.chopThreshold
                  ? <span style={{ color: 'var(--red)', fontWeight: 600 }}>{t('liveStatus.chopSuppressed')}</span>
                  : <span style={{ color: 'var(--accent)' }}>{t('liveStatus.chopTrend')}</span>
              )}
            </div>
          </div>
        )}

        {/* Strateji bazlı kartlar */}
        {indCard}

        {/* Olay Günlüğü */}
        <div className="stat-card events-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div className="stat-label" style={{ marginBottom: 0 }}>{t('liveStatus.eventLog')}</div>
            <span style={{ fontSize: 12, color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>{clock}</span>
          </div>
          <div className="events-scroll">
            {events.length === 0 ? (
              <span style={{ color: 'var(--muted)', fontSize: 12 }}>{t('liveStatus.noEvents')}</span>
            ) : events.map(ev => (
              <div key={ev.id} className="event-item">
                <span className="event-time">{timeStr(ev.timestamp)}</span>
                <span className={`event-label event-${ev.kind || 'ok'}`}>{ev.label}</span>
                {ev.detail && <span style={{ color: 'var(--muted)', fontSize: 11, marginLeft: 'auto' }}>{ev.detail}</span>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Açık Pozisyonlar */}
      {openTrades && openTrades.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <p className="card-title">{t('liveStatus.openPositions')}</p>
          <div className="positions-list">
            {openTrades.map(trade => (
              <div key={trade.id} className="position-row">
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="position-pair">{trade.symbol}</span>
                  <span className="position-sl-tp">
                    {trade.market === 'spot'
                      ? `${t('botControl.quantity')}: ${fmt(trade.quantity, 2)} USDT ${t('liveStatus.spotSuffix')}`
                      : `${t('botControl.margin')}: ${fmt(trade.margin, 2)} USDT · ${trade.leverage}x`}
                  </span>
                  <span className="position-sl-tp">
                    SL: {fmt(trade.sl_price, 4)} &nbsp;|&nbsp; TP: {fmt(trade.tp_price, 4)}
                  </span>
                </div>
                <span className={`position-side side-${trade.side}`}>{trade.side}</span>
                <div style={{ textAlign: 'right' }}>
                  <div className="position-price">${fmt(trade.entry_price, 4)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
